import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import ClientLayout from "../components/ClientLayout";
import { Download, ArrowLeft, Printer } from "lucide-react";

const WEEKDAY_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function toISODateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isoDateList(start, end) {
  if (!start || !end) return [];
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  if (isNaN(+s) || isNaN(+e) || e < s) return [];
  const out = [];
  const d = new Date(s);
  while (d <= e) {
    out.push(toISODateLocal(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}
function formatDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return {
    weekday: WEEKDAY_FULL[d.getDay()],
    short: d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }),
  };
}

export default function ClientPlan() {
  const { token, planId, nameSlug = "client" } = useParams();
  const base = `/${nameSlug}/${token}`;
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/client/${token}/plans/${planId}`).then(({ data }) => setData(data));
  }, [token, planId]);

  if (!data) return <div className="min-h-screen grid place-items-center text-[hsl(var(--brand-muted))] font-serif-italic">Loading…</div>;
  const { plan, recipes_map, client } = data;
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/client/${token}/plans/${planId}/pdf`;
  const dates = isoDateList(plan.start_date, plan.end_date);

  return (
    <ClientLayout clientName={client.name}>
      <section className="max-w-4xl mx-auto px-4 md:px-8 pt-8 md:pt-12 pb-20" data-testid="client-plan-page">
        <div className="flex items-center justify-between flex-wrap gap-3 no-print">
          <Link to={base} className="text-sm text-[hsl(var(--brand-muted))] inline-flex items-center gap-1 hover:text-[hsl(var(--brand-ink))]">
            <ArrowLeft className="w-3 h-3" /> Back
          </Link>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[hsl(var(--brand-border))] text-sm hover:bg-[hsl(var(--brand-cream-deep))]" data-testid="print-plan-btn">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[hsl(var(--brand-sage))] text-white text-sm hover:bg-[hsl(var(--brand-sage-hover))]" data-testid="download-plan-pdf-btn">
              <Download className="w-3.5 h-3.5" /> Download PDF
            </a>
          </div>
        </div>

        {/* Canva-like PDF view */}
        <div className="mt-6 bg-canva-beige relative overflow-hidden rounded-2xl" data-testid="plan-canva-view">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="relative canva-frame">
            <div className="text-center">
              <div className="font-display text-4xl md:text-6xl leading-tight tracking-tight text-[hsl(var(--brand-ink))]">
                {plan.title}
              </div>
              <div className="font-serif-italic text-xl md:text-2xl mt-2 text-[hsl(var(--brand-sage))]">
                Customised for: {client.name}
              </div>
              <div className="mt-2 text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-muted))]">
                {plan.start_date} — {plan.end_date}
              </div>
              {plan.notes ? (
                <div className="mt-3 font-serif-italic text-[hsl(var(--brand-muted))] max-w-xl mx-auto">"{plan.notes}"</div>
              ) : null}
              <div className="text-xs mt-3 text-[hsl(var(--brand-muted))]">Click underlined meals for the recipe.</div>
            </div>
            <div className="wavy-divider my-8" />

            <div className="space-y-10">
              {dates.map((iso) => {
                const { weekday, short } = formatDate(iso);
                const entries = (plan.days?.[iso]?.entries) || [];
                const withText = entries.filter((e) => (e.text || "").trim() || (e.recipe_ids || []).length > 0);
                return (
                  <div key={iso} data-testid={`day-${iso}`}>
                    <div className="flex items-baseline justify-between">
                      <div className="font-display text-2xl md:text-3xl text-[hsl(var(--brand-sage))]">{weekday}</div>
                      <div className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--brand-muted))]">{short}</div>
                    </div>
                    {withText.length === 0 ? (
                      <div className="mt-2 font-serif-italic text-[hsl(var(--brand-muted))]">Rest day — listen to your body.</div>
                    ) : (
                      <div className="mt-3 border-y border-[hsl(var(--brand-sage))]/30">
                        <div className="grid grid-cols-[90px_1fr] md:grid-cols-[110px_1.4fr_1fr] py-2 px-1 text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--brand-muted))]">
                          <div>Time</div>
                          <div>Meal</div>
                          <div className="hidden md:block">Remarks</div>
                        </div>
                        {withText.map((entry) => {
                          const recipes = (entry.recipe_ids || []).map((rid) => recipes_map[rid]).filter(Boolean);
                          const recipeNames = recipes.map((r) => (r.name || "").trim().toLowerCase());
                          const rawText = (entry.text || "").trim();
                          // Hide entry.text if it duplicates one of the attached recipe names
                          const extraText = rawText && !recipeNames.includes(rawText.toLowerCase()) ? rawText : "";
                          return (
                            <div key={entry.id} className="grid grid-cols-[90px_1fr] md:grid-cols-[110px_1.4fr_1fr] py-3 px-1 border-t border-[hsl(var(--brand-sage))]/15" data-testid={`meal-${iso}-${entry.id}`}>
                              <div className="text-sm text-[hsl(var(--brand-ink))]/80">{entry.time || ""}</div>
                              <div className="text-[15px] text-[hsl(var(--brand-ink))]">
                                {entry.label ? (
                                  <div className="font-medium text-sm uppercase tracking-wider text-[hsl(var(--brand-muted))] mb-0.5">{entry.label}</div>
                                ) : null}
                                {recipes.length > 0 ? (
                                  <div className="flex flex-col items-start gap-0.5" data-testid={`recipes-${iso}-${entry.id}`}>
                                    {recipes.map((r) => (
                                      <Link key={r.id} to={`${base}/recipes/${r.id}`} className="recipe-link" data-testid={`recipe-link-${iso}-${entry.id}-${r.id}`}>
                                        {r.name}
                                      </Link>
                                    ))}
                                  </div>
                                ) : null}
                                {extraText ? (
                                  recipes.length > 0 ? (
                                    <div className="mt-1 text-[15px] text-[hsl(var(--brand-ink))] whitespace-pre-line" data-testid={`meal-text-${iso}-${entry.id}`}>{extraText}</div>
                                  ) : (
                                    <div className="whitespace-pre-line" data-testid={`meal-text-${iso}-${entry.id}`}>{extraText}</div>
                                  )
                                ) : null}
                                {entry.remarks ? (
                                  <div className="md:hidden text-sm text-[hsl(var(--brand-muted))] font-serif-italic mt-1 whitespace-pre-line">{entry.remarks}</div>
                                ) : null}
                              </div>
                              <div className="hidden md:block text-sm text-[hsl(var(--brand-muted))] font-serif-italic whitespace-pre-line">
                                {entry.remarks || ""}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="wavy-divider mt-10" />
            <div className="text-center mt-6">
              <div className="font-display text-xl text-[hsl(var(--brand-ink))]">Dt. Amisha Kaushal</div>
              <div className="text-xs text-[hsl(var(--brand-muted))] tracking-[0.2em]">+91 98729 52869</div>
            </div>
          </div>
        </div>
      </section>
    </ClientLayout>
  );
}
